import React, { useCallback, useEffect, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Space, Table, Tabs, Tag, Typography } from 'antd';
import { message } from '../../utils/antdMessage';
import { confirmDangerousAction } from './dangerConfirm';
import {
  PanoramaAdminPanel,
  canManagePanoramaAdmin
} from './systemSettings/panoramaManageTab';
import LayerSettingsPanel from './layerSettingsPanel';
import ContourLodSettingsPanel from './contourLodSettingsPanel';

const { Text } = Typography;

function formatBounds(b) {
  if (!b) return '-';
  let obj = b;
  if (typeof b === 'string') {
    try { obj = JSON.parse(b); } catch (_) { return String(b).slice(0, 40); }
  }
  const west = Number(obj.west ?? (Array.isArray(obj) ? obj[0] : NaN));
  const south = Number(obj.south ?? (Array.isArray(obj) ? obj[1] : NaN));
  const east = Number(obj.east ?? (Array.isArray(obj) ? obj[2] : NaN));
  const north = Number(obj.north ?? (Array.isArray(obj) ? obj[3] : NaN));
  if (![west, south, east, north].every(Number.isFinite)) return '-';
  return `${west.toFixed(4)},${south.toFixed(4)} → ${east.toFixed(4)},${north.toFixed(4)}`;
}

function formatTime(v) {
  if (!v) return '-';
  try {
    return new Date(v).toLocaleString('zh-CN', { hour12: false });
  } catch (_) {
    return String(v);
  }
}

/** 编辑成品名称 / 描述 */
function ProductEditModal({ open, row, saving, onCancel, onSave }) {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: row?.name || '',
      description: row?.description || ''
    });
  }, [open, row, form]);

  return (
    <Modal
      open={open}
      title={`编辑成品${row?.id != null ? ` #${row.id}` : ''}`}
      okText="保存"
      cancelText="取消"
      confirmLoading={saving}
      onCancel={onCancel}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => onSave?.(values)}
      >
        <Form.Item
          name="name"
          label="显示名称"
          rules={[{ required: true, message: '请填写显示名称' }]}
          extra="指挥台「地图叠加」竖型菜单中显示"
        >
          <Input maxLength={200} placeholder="例如：丽水市等高线" />
        </Form.Item>
        <Form.Item
          name="description"
          label="描述"
          extra="鼠标悬停菜单名称时显示（可选）"
        >
          <Input.TextArea rows={3} maxLength={500} showCount placeholder="用途、范围、注意事项等" />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/** 后台改 3D model_pose（无地图预览；指挥台「微调」可边看边调） */
function ModelPoseEditModal({ open, row, saving, onCancel, onSave }) {
  const [form] = Form.useForm();
  const pose = row?.modelPose || {};

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      lng: Number.isFinite(Number(pose.lng)) ? Number(pose.lng) : undefined,
      lat: Number.isFinite(Number(pose.lat)) ? Number(pose.lat) : undefined,
      altitude: Number.isFinite(Number(pose.altitude)) ? Number(pose.altitude) : 0,
      rotateX: Number.isFinite(Number(pose.rotateX)) ? Number(pose.rotateX) : 0,
      rotateY: Number.isFinite(Number(pose.rotateY)) ? Number(pose.rotateY) : 0,
      rotateZ: Number.isFinite(Number(pose.rotateZ)) ? Number(pose.rotateZ) : 0,
      scale: Number.isFinite(Number(pose.scale)) && Number(pose.scale) > 0 ? Number(pose.scale) : 1
    });
  }, [open, row, form, pose.lng, pose.lat, pose.altitude, pose.rotateX, pose.rotateY, pose.rotateZ, pose.scale]);

  return (
    <Modal
      open={open}
      title={`3D 位姿${row?.id != null ? ` · #${row.id}` : ''}`}
      okText="保存"
      cancelText="取消"
      confirmLoading={saving}
      onCancel={onCancel}
      onOk={() => form.submit()}
      destroyOnClose
      width={480}
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        旋转单位为度。需要对照底图时请到指挥台叠加后点「微调」。
      </Text>
      <Form form={form} layout="vertical" size="small" onFinish={(v) => onSave?.(v)}>
        <Form.Item name="lng" label="经度 lng" rules={[{ required: true, message: '必填' }]}>
          <InputNumber style={{ width: '100%' }} step={0.00001} precision={6} />
        </Form.Item>
        <Form.Item name="lat" label="纬度 lat" rules={[{ required: true, message: '必填' }]}>
          <InputNumber style={{ width: '100%' }} step={0.00001} precision={6} />
        </Form.Item>
        <Form.Item name="altitude" label="海拔 altitude (m)">
          <InputNumber style={{ width: '100%' }} step={0.5} precision={1} />
        </Form.Item>
        <Form.Item name="rotateX" label="绕 X (°)"><InputNumber style={{ width: '100%' }} step={1} /></Form.Item>
        <Form.Item name="rotateY" label="绕 Y (°)"><InputNumber style={{ width: '100%' }} step={1} /></Form.Item>
        <Form.Item name="rotateZ" label="绕 Z (°)"><InputNumber style={{ width: '100%' }} step={1} /></Form.Item>
        <Form.Item name="scale" label="缩放 scale" rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} min={0.05} max={5} step={0.01} precision={3} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/** 路网 / 等高线成品表 */
function MapProductPanel({ apiRequest, kind }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!apiRequest) return;
    setLoading(true);
    try {
      const data = await apiRequest(`/api/map-layers/admin/products?kind=${encodeURIComponent(kind)}`);
      if (!data.success) {
        message.error(data.error || '加载失败');
        return;
      }
      setRows(data.layers || []);
    } finally {
      setLoading(false);
    }
  }, [apiRequest, kind]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (values) => {
    if (!editRow?.id) return;
    setSaving(true);
    try {
      const data = await apiRequest(`/api/map-layers/admin/products/${editRow.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: String(values.name || '').trim(),
          description: String(values.description || '').trim()
        })
      });
      if (!data.success) {
        message.error(data.error || '保存失败');
        return;
      }
      message.success(data.message || '已保存');
      setEditRow(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await confirmDangerousAction({
        title: `删除${
          kind === 'contour' ? '等高线'
            : kind === 'poi' ? '地标'
              : kind === 'building' ? '建筑'
                : '路网'
        }成品 #${row.id}？`,
        content: (
          <div>
            <p>名称：{row.name || '-'}（v{row.version}）</p>
            <p>将删除数据库记录，并清理 OSS 目录：</p>
            <Text code>layers/map-{kind}/task-{row.taskId}/v{row.version}/</Text>
            <p style={{ marginTop: 8, color: '#b45309' }}>此操作不可恢复。</p>
          </div>
        )
      });
    } catch (_) {
      return;
    }
    const data = await apiRequest(`/api/map-layers/admin/products/${row.id}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirm_text: '确认删除' })
    });
    if (!data.success) {
      message.error(data.error || '删除失败');
      return;
    }
    const purge = data.ossPurge || {};
    message.success(
      purge.skipped
        ? `${data.message}（OSS 跳过：${purge.reason || purge.error || '-'}）`
        : `${data.message}，已清理 OSS ${purge.deleted || 0} 个对象`
    );
    load();
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '名称', dataIndex: 'name', ellipsis: true },
    {
      title: '描述',
      dataIndex: 'description',
      ellipsis: true,
      render: (v) => v || <Text type="secondary">-</Text>
    },
    { title: '版本', dataIndex: 'version', width: 70 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (s) => (
        <Tag color={s === 'active' ? 'green' : 'default'}>{s || '-'}</Tag>
      )
    },
    { title: '任务', dataIndex: 'taskId', width: 80 },
    {
      title: '范围',
      dataIndex: 'bounds',
      ellipsis: true,
      render: formatBounds
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 160,
      render: formatTime
    },
    {
      title: '操作',
      key: 'act',
      width: 140,
      render: (_, row) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => setEditRow(row)}>
            编辑
          </Button>
          <Button type="link" danger size="small" onClick={() => handleDelete(row)}>
            删除
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button size="small" onClick={load} loading={loading}>刷新</Button>
        <Text type="secondary">可改名称与描述；删除将同步清理 OSS 成果文件</Text>
      </Space>
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
      <ProductEditModal
        open={!!editRow}
        row={editRow}
        saving={saving}
        onCancel={() => setEditRow(null)}
        onSave={handleSave}
      />
    </div>
  );
}

/** 2D / 3D 航拍成品表（相互独立：删 2D 不动 GLB；3D 只列原生模型版本） */
function OrthoProductPanel({ apiRequest, mode }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [poseRow, setPoseRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const isGlb = mode === 'glb';

  const load = useCallback(async () => {
    if (!apiRequest) return;
    setLoading(true);
    try {
      const kind = isGlb ? '3d' : '2d';
      const data = await apiRequest(`/api/ortho/admin/products?kind=${kind}`);
      if (!data.success) {
        message.error(data.error || '加载失败');
        return;
      }
      setRows(data.layers || []);
    } finally {
      setLoading(false);
    }
  }, [apiRequest, isGlb]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (values) => {
    if (!editRow?.id) return;
    setSaving(true);
    try {
      const data = await apiRequest(`/api/ortho/admin/products/${editRow.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: String(values.name || '').trim(),
          description: String(values.description || '').trim()
        })
      });
      if (!data.success) {
        message.error(data.error || '保存失败');
        return;
      }
      message.success(data.message || '已保存');
      setEditRow(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const handlePoseSave = async (values) => {
    if (!poseRow?.id) return;
    setSaving(true);
    try {
      const data = await apiRequest(`/api/ortho/layers/${poseRow.id}/model-pose`, {
        method: 'PATCH',
        body: JSON.stringify(values)
      });
      if (!data.success) {
        message.error(data.error || '保存位姿失败');
        return;
      }
      message.success('位姿已保存');
      setPoseRow(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await confirmDangerousAction({
        title: isGlb
          ? `删除 3D 成品 v${row.glbVersion || row.version}？`
          : `删除 2D 成品 v${row.version}？`,
        content: (
          <div>
            <p>名称：{row.name || '-'}（图层 #{row.id}）</p>
            {isGlb ? (
              <>
                <p>将删除 OSS 模型文件，并清除所有图层对该模型的登记：</p>
                <Text code>{row.glbOssKey || '-'}</Text>
                <p style={{ marginTop: 8 }}>不会删除同版本或其它版本的 2D 正射瓦片。</p>
              </>
            ) : (
              <>
                <p>将仅清理该版本正射瓦片：</p>
                <Text code>
                  layers/task-{row.taskId || '?'}/v{row.version}/tiles/
                </Text>
                {row.nativeGlb ? (
                  <p style={{ marginTop: 8, color: '#1677ff' }}>
                    本版承载原生 3D，删除后 3D 仍保留在「3D建模」列表中。
                  </p>
                ) : row.glbInherited ? (
                  <p style={{ marginTop: 8 }}>
                    本行仅继承了 v{row.glbVersion} 的 3D 引用，删除不影响该 3D 文件。
                  </p>
                ) : null}
              </>
            )}
            <p style={{ marginTop: 8, color: '#b45309' }}>此操作不可恢复。</p>
          </div>
        )
      });
    } catch (_) {
      return;
    }
    const data = await apiRequest(`/api/ortho/admin/products/${row.id}`, {
      method: 'DELETE',
      body: JSON.stringify({
        confirm_text: '确认删除',
        mode: isGlb ? 'glb' : '2d'
      })
    });
    if (!data.success) {
      message.error(data.error || '删除失败');
      return;
    }
    if (isGlb) {
      message.success(
        data.glbDeleted
          ? `${data.message}，已删 OSS GLB`
          : data.message
      );
    } else {
      const purge = data.ossPurge || {};
      if (purge.skipped) {
        message.warning(
          `${data.message}。注意：OSS 文件未清理（${purge.reason || purge.error || '原因未知'}），对象可能仍占存储`
        );
      } else {
        message.success(`${data.message}，已清理瓦片 ${purge.deleted || 0} 个对象`);
      }
    }
    load();
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    { title: '名称', dataIndex: 'name', ellipsis: true },
    {
      title: '描述',
      dataIndex: 'description',
      ellipsis: true,
      render: (v) => v || <Text type="secondary">-</Text>
    },
    {
      title: '版本',
      width: 80,
      render: (_, row) => (isGlb ? (row.glbVersion || row.version) : row.version)
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 90,
      render: (s) => (
        <Tag color={s === 'active' ? 'green' : 'default'}>{s || '-'}</Tag>
      )
    },
    { title: '任务', dataIndex: 'taskId', width: 80, render: (v) => v ?? '-' },
    ...(isGlb
      ? [{
          title: 'GLB',
          dataIndex: 'glbOssKey',
          ellipsis: true,
          render: (v) => v || '-'
        }]
      : [{
          title: '3D引用',
          width: 110,
          render: (_, row) => {
            if (row.nativeGlb) return <Tag color="blue">本版有3D</Tag>;
            if (row.glbInherited) return <Tag>借用v{row.glbVersion}</Tag>;
            return <Tag>无</Tag>;
          }
        }]),
    {
      title: '范围',
      dataIndex: 'bounds',
      ellipsis: true,
      render: formatBounds
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      width: 160,
      render: formatTime
    },
    {
      title: '操作',
      key: 'act',
      width: isGlb ? 200 : 140,
      render: (_, row) => (
        <Space size={0}>
          <Button type="link" size="small" onClick={() => setEditRow(row)}>
            编辑
          </Button>
          {isGlb && row.glbOssKey && (
            <Button type="link" size="small" onClick={() => setPoseRow(row)}>
              位姿
            </Button>
          )}
          <Button type="link" danger size="small" onClick={() => handleDelete(row)}>
            {isGlb ? '删除3D' : '删除'}
          </Button>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Space style={{ marginBottom: 12 }}>
        <Button size="small" onClick={load} loading={loading}>刷新</Button>
        <Text type="secondary">
          {isGlb
            ? '可改名称、描述与位姿；删除只清模型文件'
            : '可改名称与描述；删除只清该版本正射瓦片'}
        </Text>
      </Space>
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
      <ProductEditModal
        open={!!editRow}
        row={editRow}
        saving={saving}
        onCancel={() => setEditRow(null)}
        onSave={handleSave}
      />
      <ModelPoseEditModal
        open={!!poseRow}
        row={poseRow}
        saving={saving}
        onCancel={() => setPoseRow(null)}
        onSave={handlePoseSave}
      />
    </div>
  );
}

/** 构建「叠加图层」标签页（原成品管理：路网/等高线/航拍/全景 + 图层设置） */
export function buildProductLayersTab(context) {
  const {
    shouldShowPage,
    apiRequest,
    mapSettings,
    setMapSettings,
    handleSaveMapSettings,
    mapSettingsMsg
  } = context;
  const canProducts = shouldShowPage('成品管理');
  const canPanorama = canManagePanoramaAdmin(shouldShowPage);
  const canLayerSettings = canProducts || canPanorama || shouldShowPage('地图设置');
  if (!canProducts && !canPanorama && !canLayerSettings) return null;

  const items = [];
  if (canProducts) {
    items.push(
      {
        key: 'road',
        label: '路网',
        children: (
          <Tabs
            size="small"
            type="card"
            style={{ marginTop: 4 }}
            items={[
              {
                key: 'road-main',
                label: '路网',
                children: <MapProductPanel apiRequest={apiRequest} kind="road" />
              },
              {
                key: 'road-poi',
                label: '地标',
                children: <MapProductPanel apiRequest={apiRequest} kind="poi" />
              },
              {
                key: 'road-building',
                label: '建筑',
                children: <MapProductPanel apiRequest={apiRequest} kind="building" />
              }
            ]}
          />
        )
      },
      {
        key: 'contour',
        label: '等高线',
        children: (
          <div>
            <ContourLodSettingsPanel
              mapSettings={mapSettings || {}}
              setMapSettings={setMapSettings}
              handleSaveMapSettings={handleSaveMapSettings}
              mapSettingsMsg={mapSettingsMsg}
            />
            <MapProductPanel apiRequest={apiRequest} kind="contour" />
          </div>
        )
      },
      {
        key: 'ortho2d',
        label: '2D航拍',
        children: <OrthoProductPanel apiRequest={apiRequest} mode="2d" />
      },
      {
        key: 'ortho3d',
        label: '3D建模',
        children: <OrthoProductPanel apiRequest={apiRequest} mode="glb" />
      }
    );
  }
  if (canPanorama) {
    items.push({
      key: 'panorama',
      label: '全景影像',
      children: <PanoramaAdminPanel />
    });
  }
  if (canLayerSettings) {
    items.push({
      key: 'layer-settings',
      label: '图层设置',
      children: (
        <LayerSettingsPanel
          mapSettings={mapSettings || {}}
          setMapSettings={setMapSettings}
          handleSaveMapSettings={handleSaveMapSettings}
          mapSettingsMsg={mapSettingsMsg}
        />
      )
    });
  }

  return {
    key: 'product-layers',
    label: '叠加图层',
    children: (
      <div>
        <div style={{ marginBottom: 12, color: '#666', fontSize: 13 }}>
          管理指挥台已发布的路网、等高线、2D 航拍、3D 建模与全景影像等叠加图层；
          「图层设置」可配置点击成品名称时的飞行方式与全景地图挂点。
          路网/等高线/航拍可在此重命名、修改悬停描述；删除前需输入「确认删除」，并会清理 OSS 残留文件。
          新建/切片/建模仍在指挥台「地图叠加」工具箱完成。
        </div>
        <Tabs size="small" items={items} />
      </div>
    )
  };
}
